import React, { useEffect, useMemo, useState } from "react";
import { api, formatCHF, currentMonthString, formatMonat, formatDatum } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { PageContainer, PageHeader, MonthPicker } from "../components/PageHeader";
import { Plus, Trash2, Wallet, CheckCircle2, RefreshCw, UserPlus } from "lucide-react";
import { toast } from "sonner";

const ADJUSTMENT_TYPES = [
  { value: "abzug", label: "Abzug", color: "text-red-600" },
  { value: "fahrzeug_entschaedigung", label: "Fahrzeug-Entschädigung", color: "text-blue-700" },
  { value: "bonus", label: "Bonus", color: "text-green-700" },
  { value: "sonstiges", label: "Sonstige Zulage", color: "text-neutral-700" },
];
const PAYMENT_METHODS = [
  { value: "bank", label: "Bank" },
  { value: "bar", label: "Bar" },
  { value: "twint", label: "Twint" },
  { value: "sonstiges", label: "Sonstiges" },
];

function statusBadge(s) {
  if (s === "bezahlt") return <Badge className="bg-[#008A00] text-white">Bezahlt</Badge>;
  if (s === "teilweise") return <Badge className="bg-[#FFB800] text-neutral-900">Teilweise</Badge>;
  return <Badge variant="outline">Offen</Badge>;
}

export default function Fahrerloehne() {
  const [monat, setMonat] = useState(currentMonthString());
  const [summary, setSummary] = useState({ fahrer: [], grand: {} });
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openDetail, setOpenDetail] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [reloading, setReloading] = useState(false);

  const load = async () => {
    const [s, r, e] = await Promise.all([
      api.get("/payroll-summary", { params: { monat } }),
      api.get("/payroll-records", { params: { monat } }),
      api.get("/employees"),
    ]);
    setSummary(s.data);
    setRecords(r.data);
    setEmployees(e.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monat]);

  const recalc = async () => {
    setReloading(true);
    try {
      await load();
      toast.success("Löhne neu berechnet.");
    } catch (e) {
      toast.error("Neuberechnung fehlgeschlagen.");
    } finally {
      setReloading(false);
    }
  };

  const recordByFahrer = useMemo(() => {
    const m = {};
    for (const r of records) m[r.fahrer_id] = r;
    return m;
  }, [records]);

  // Merged rows: Fahrer mit Tour-Basis + manuelle Einträge ohne Tour-Basis
  const rows = useMemo(() => {
    const out = (summary.fahrer || []).map((f) => ({ id: f.id, name: f.name, tourBasis: f.total_basis || 0 }));
    const known = new Set(out.map((r) => r.id));
    for (const r of records) {
      if (!known.has(r.fahrer_id)) {
        out.push({ id: r.fahrer_id, name: r.fahrer_name || "Unbekannt", tourBasis: 0, manualOnly: true });
      }
    }
    return out;
  }, [summary, records]);

  const totals = useMemo(() => {
    let basis = 0, netto = 0, bezahlt = 0, offen = 0;
    for (const row of rows) {
      const rec = recordByFahrer[row.id];
      const b = rec ? (rec.basis_brutto || 0) : row.tourBasis;
      basis += b;
      if (rec) {
        netto += rec.netto_auszahlung || 0;
        bezahlt += rec.bezahlt_total || 0;
        offen += rec.offen || 0;
      } else {
        netto += b;
        offen += b;
      }
    }
    return { basis, netto, bezahlt, offen };
  }, [rows, recordByFahrer]);

  return (
    <PageContainer>
      <PageHeader title="Fahrerlöhne" subtitle="Abzüge, Zulagen (z.B. Fahrzeug-Entschädigung) und Auszahlungen pro Fahrer & Monat" testId="payroll-page">
        <MonthPicker value={monat} onChange={setMonat} testId="payroll-month-picker" />
        <Button variant="outline" onClick={recalc} disabled={reloading} className="border-neutral-300" data-testid="payroll-recalc">
          <RefreshCw className={`h-4 w-4 mr-2 ${reloading ? "animate-spin" : ""}`} /> Neu berechnen
        </Button>
        <Button onClick={() => setManualOpen(true)} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="payroll-manual-entry">
          <UserPlus className="h-4 w-4 mr-2" /> Manueller Eintrag
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="border-neutral-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Basis-Brutto</div>
          <div className="text-xl font-semibold nivara-num mt-1" data-testid="payroll-total-basis">{formatCHF(totals.basis)}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Netto-Auszahlung</div>
          <div className="text-xl font-semibold nivara-num mt-1" data-testid="payroll-total-netto">{formatCHF(totals.netto)}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Bezahlt</div>
          <div className="text-xl font-semibold nivara-num text-green-700 mt-1" data-testid="payroll-total-bezahlt">{formatCHF(totals.bezahlt)}</div>
        </Card>
        <Card className="border-neutral-200 bg-white p-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Offen</div>
          <div className="text-xl font-semibold nivara-num text-amber-700 mt-1" data-testid="payroll-total-offen">{formatCHF(totals.offen)}</div>
        </Card>
      </div>

      <Card className="border-neutral-200 bg-white overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <Table data-testid="payroll-table">
            <TableHeader>
              <TableRow>
                <TableHead>Fahrer</TableHead>
                <TableHead className="text-right">Basis</TableHead>
                <TableHead className="text-right">Abzüge</TableHead>
                <TableHead className="text-right">Zulagen</TableHead>
                <TableHead className="text-right">Netto</TableHead>
                <TableHead className="text-right">Bezahlt</TableHead>
                <TableHead className="text-right">Offen</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-8">Keine Lohnbasis für {formatMonat(monat)}.</TableCell></TableRow>
              )}
              {rows.map((row) => {
                const rec = recordByFahrer[row.id];
                const basis = rec ? (rec.basis_brutto || 0) : row.tourBasis;
                const abzuege = rec?.summe_abzuege || 0;
                const zulagen = rec?.summe_zulagen || 0;
                const netto = rec ? rec.netto_auszahlung : basis;
                const bezahlt = rec?.bezahlt_total || 0;
                const offen = rec ? rec.offen : basis;
                const status = rec?.status || "offen";
                const isManual = rec?.manuelle_basis != null;
                return (
                  <TableRow key={row.id} className="cursor-pointer hover:bg-neutral-50" onClick={() => setOpenDetail(row.id)} data-testid={`payroll-row-${row.id}`}>
                    <TableCell className="font-medium">
                      {row.name}
                      {isManual && <Badge variant="outline" className="ml-2 text-[10px] border-blue-300 text-blue-700">Manuell</Badge>}
                    </TableCell>
                    <TableCell className="text-right nivara-num">{formatCHF(basis)}</TableCell>
                    <TableCell className="text-right nivara-num text-red-600">{abzuege ? "– " + formatCHF(abzuege) : "—"}</TableCell>
                    <TableCell className="text-right nivara-num text-blue-700">{zulagen ? "+ " + formatCHF(zulagen) : "—"}</TableCell>
                    <TableCell className="text-right nivara-num font-semibold">{formatCHF(netto)}</TableCell>
                    <TableCell className="text-right nivara-num text-green-700">{formatCHF(bezahlt)}</TableCell>
                    <TableCell className="text-right nivara-num text-amber-700">{formatCHF(offen)}</TableCell>
                    <TableCell>{statusBadge(status)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setOpenDetail(row.id); }}>
                        Öffnen
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Mobile card list */}
      <div className="md:hidden space-y-2" data-testid="payroll-cards">
        {rows.length === 0 && (
          <Card className="border-neutral-200 bg-white p-6 text-center text-neutral-500">Keine Lohnbasis für {formatMonat(monat)}.</Card>
        )}
        {rows.map((row) => {
          const rec = recordByFahrer[row.id];
          const basis = rec ? (rec.basis_brutto || 0) : row.tourBasis;
          const abzuege = rec?.summe_abzuege || 0;
          const zulagen = rec?.summe_zulagen || 0;
          const netto = rec ? rec.netto_auszahlung : basis;
          const bezahlt = rec?.bezahlt_total || 0;
          const offen = rec ? rec.offen : basis;
          const status = rec?.status || "offen";
          const isManual = rec?.manuelle_basis != null;
          return (
            <Card
              key={row.id}
              className="border-neutral-200 bg-white p-3 active:bg-neutral-50 cursor-pointer"
              onClick={() => setOpenDetail(row.id)}
              data-testid={`payroll-card-${row.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-base">
                  {row.name}
                  {isManual && <Badge variant="outline" className="ml-2 text-[10px] border-blue-300 text-blue-700">Manuell</Badge>}
                </div>
                {statusBadge(status)}
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <div className="text-neutral-500">Basis</div>
                <div className="text-right nivara-num">{formatCHF(basis)}</div>
                {abzuege > 0 && (<>
                  <div className="text-neutral-500">Abzüge</div>
                  <div className="text-right nivara-num text-red-600">– {formatCHF(abzuege)}</div>
                </>)}
                {zulagen > 0 && (<>
                  <div className="text-neutral-500">Zulagen</div>
                  <div className="text-right nivara-num text-blue-700">+ {formatCHF(zulagen)}</div>
                </>)}
                <div className="text-neutral-700 font-semibold border-t border-neutral-200 pt-1">Netto</div>
                <div className="text-right nivara-num font-semibold border-t border-neutral-200 pt-1">{formatCHF(netto)}</div>
                <div className="text-neutral-500">Bezahlt</div>
                <div className="text-right nivara-num text-green-700">{formatCHF(bezahlt)}</div>
                <div className="text-neutral-500">Offen</div>
                <div className={`text-right nivara-num font-semibold ${offen > 0.01 ? "text-amber-700" : offen < -0.01 ? "text-red-600" : "text-green-700"}`}>
                  {formatCHF(offen)}
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      {openDetail && (
        <PayrollDetailDialog
          monat={monat}
          fahrerId={openDetail}
          fahrerName={rows.find((r) => r.id === openDetail)?.name || ""}
          onClose={() => { setOpenDetail(null); load(); }}
        />
      )}

      {manualOpen && (
        <ManualEntryDialog
          monat={monat}
          employees={employees}
          existingIds={new Set(rows.map((r) => r.id))}
          onClose={(saved) => { setManualOpen(false); if (saved) load(); }}
        />
      )}

      <div className="text-xs text-neutral-500 mt-3">
        Tipp: Klicke eine Zeile, um Abzüge, Zulagen (z.B. Fahrzeug-Entschädigung) und Auszahlungen für diesen Fahrer und Monat zu pflegen.
        Bereits ausbezahlte Monate (z.B. Mai 2026) bleiben unangetastet, solange du dort nichts speicherst.
      </div>
    </PageContainer>
  );
}


function ManualEntryDialog({ monat, employees, existingIds, onClose }) {
  const [fahrerId, setFahrerId] = useState("");
  const [betrag, setBetrag] = useState("");
  const [bemerkung, setBemerkung] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!fahrerId) { toast.error("Bitte einen Fahrer wählen."); return; }
    const amount = Number(String(betrag).replace(",", "."));
    if (!amount || amount <= 0) { toast.error("Bitte einen gültigen Betrag eingeben."); return; }
    setSaving(true);
    try {
      // Bestehende Abzüge/Zulagen/Zahlungen übernehmen (falls Record existiert)
      const existing = await api.get(`/payroll-records/${monat}/${fahrerId}`);
      const ex = existing.data;
      await api.post("/payroll-records", {
        monat,
        fahrer_id: fahrerId,
        adjustments: ex.is_preview ? [] : (ex.adjustments || []),
        payments: ex.is_preview ? [] : (ex.payments || []),
        bemerkung: bemerkung || ex.bemerkung || "",
        manuelle_basis: amount,
      });
      toast.success("Manueller Lohn-Eintrag gespeichert.");
      onClose(true);
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-md" data-testid="manual-entry-dialog">
        <DialogHeader>
          <DialogTitle>Manueller Lohn-Eintrag · {formatMonat(monat)}</DialogTitle>
          <DialogDescription>
            Für Monate ohne Tour-Daten oder um die berechnete Basis zu überschreiben. Der Betrag gilt als Lohnbasis (Brutto) für diesen Fahrer und Monat.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Fahrer</Label>
            <Select value={fahrerId} onValueChange={setFahrerId}>
              <SelectTrigger data-testid="manual-entry-fahrer"><SelectValue placeholder="Fahrer wählen…" /></SelectTrigger>
              <SelectContent>
                {employees.filter((e) => e.aktiv !== false).map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}{existingIds.has(e.id) ? " (hat bereits Basis/Eintrag)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Lohnbasis (CHF)</Label>
            <Input type="number" step="0.01" min="0" value={betrag} onChange={(e) => setBetrag(e.target.value)} className="font-mono" placeholder="z.B. 1500.00" data-testid="manual-entry-betrag" />
          </div>
          <div className="space-y-1">
            <Label>Bemerkung</Label>
            <Textarea value={bemerkung} onChange={(e) => setBemerkung(e.target.value)} placeholder="z.B. Pauschallohn Juli" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="manual-entry-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function PayrollDetailDialog({ monat, fahrerId, fahrerName, onClose }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = await api.get(`/payroll-records/${monat}/${fahrerId}`);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monat, fahrerId]);

  if (!data) return null;

  const update = (patch) => setData((d) => ({ ...d, ...patch }));

  const addAdjustment = (typ) => {
    const defaultLabel = typ === "abzug" ? "Qualitätsabzug" : typ === "fahrzeug_entschaedigung" ? "Fahrzeug-Entschädigung" : typ === "bonus" ? "Bonus" : "Sonstiges";
    update({ adjustments: [...(data.adjustments || []), { typ, label: defaultLabel, betrag: 0, bemerkung: "" }] });
  };
  const updateAdjustment = (i, patch) => update({ adjustments: data.adjustments.map((a, idx) => idx === i ? { ...a, ...patch } : a) });
  const removeAdjustment = (i) => update({ adjustments: data.adjustments.filter((_, idx) => idx !== i) });

  const addPayment = (methode) => {
    update({ payments: [...(data.payments || []), { methode, betrag: 0, datum: new Date().toISOString().slice(0, 10), referenz: "" }] });
  };
  const updatePayment = (i, patch) => update({ payments: data.payments.map((p, idx) => idx === i ? { ...p, ...patch } : p) });
  const removePayment = (i) => update({ payments: data.payments.filter((_, idx) => idx !== i) });

  const d = (() => {
    const adj = data.adjustments || [];
    const pay = data.payments || [];
    const abzuege = adj.filter(a => a.typ === "abzug").reduce((s, a) => s + (Number(a.betrag) || 0), 0);
    const zulagen = adj.filter(a => a.typ !== "abzug").reduce((s, a) => s + (Number(a.betrag) || 0), 0);
    const basisEff = data.manuelle_basis != null && data.manuelle_basis !== ""
      ? (Number(data.manuelle_basis) || 0)
      : (data.basis_brutto || 0);
    const netto = basisEff - abzuege + zulagen;
    const bezahlt = pay.reduce((s, p) => s + (Number(p.betrag) || 0), 0);
    const offen = netto - bezahlt;
    return { abzuege, zulagen, netto, bezahlt, offen, basisEff };
  })();

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/payroll-records", {
        monat: data.monat,
        fahrer_id: data.fahrer_id,
        adjustments: (data.adjustments || []).map((a) => ({ ...a, betrag: Number(a.betrag) || 0 })),
        payments: (data.payments || []).map((p) => ({ ...p, betrag: Number(p.betrag) || 0 })),
        bemerkung: data.bemerkung || "",
        manuelle_basis: data.manuelle_basis == null || data.manuelle_basis === "" ? null : Number(data.manuelle_basis),
      });
      toast.success("Gespeichert");
      setData({ ...r.data });
      onClose();
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const payRemaining = async (methode) => {
    if (d.offen <= 0.01) {
      toast.info("Bereits voll bezahlt.");
      return;
    }
    try {
      // Always save first to ensure a DB record exists, then call pay-full
      const r = await api.post("/payroll-records", {
        monat: data.monat,
        fahrer_id: data.fahrer_id,
        adjustments: (data.adjustments || []).map((a) => ({ ...a, betrag: Number(a.betrag) || 0 })),
        payments: (data.payments || []).map((p) => ({ ...p, betrag: Number(p.betrag) || 0 })),
        bemerkung: data.bemerkung || "",
        manuelle_basis: data.manuelle_basis == null || data.manuelle_basis === "" ? null : Number(data.manuelle_basis),
      });
      const r2 = await api.post(`/payroll-records/${r.data.id}/pay-full`, { methode });
      setData({ ...r2.data, is_preview: false });
      toast.success(`Restbetrag bezahlt (${methode})`);
    } catch (e) {
      toast.error("Fehler: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="payroll-dialog">
        <DialogHeader>
          <DialogTitle>Lohn-Detail · {fahrerName} · {formatMonat(monat)}</DialogTitle>
          <DialogDescription>
            Basis wird aus den Touren automatisch berechnet. Hier nur Abzüge, Zulagen und Auszahlungen pflegen.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 my-3">
          <Card className="p-2 border-neutral-200">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Basis{data.manuelle_basis != null ? " (manuell)" : ""}</div>
            <div className="font-semibold nivara-num">{formatCHF(d.basisEff)}</div>
          </Card>
          <Card className="p-2 border-neutral-200">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Abzüge</div>
            <div className="font-semibold nivara-num text-red-600">– {formatCHF(d.abzuege)}</div>
          </Card>
          <Card className="p-2 border-neutral-200">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Zulagen</div>
            <div className="font-semibold nivara-num text-blue-700">+ {formatCHF(d.zulagen)}</div>
          </Card>
          <Card className="p-2 border-neutral-200 bg-neutral-50">
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Netto</div>
            <div className="font-semibold nivara-num">{formatCHF(d.netto)}</div>
          </Card>
          <Card className={`p-2 ${d.offen < 0.01 ? "bg-green-50 border-green-300" : "bg-amber-50 border-amber-300"}`}>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Offen</div>
            <div className="font-semibold nivara-num">{formatCHF(d.offen)}</div>
          </Card>
        </div>

        {/* Manuelle Basis */}
        <div className="mt-1 p-2 rounded-md border border-neutral-200 bg-neutral-50">
          {data.manuelle_basis != null ? (
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <Label className="text-xs">Manuelle Basis (CHF) — überschreibt Tour-Basis</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={data.manuelle_basis}
                  onChange={(e) => update({ manuelle_basis: e.target.value })}
                  className="font-mono w-40"
                  data-testid="payroll-manual-basis-input"
                />
              </div>
              <Button size="sm" variant="outline" onClick={() => update({ manuelle_basis: null })} data-testid="payroll-manual-basis-remove">
                Entfernen (Tour-Basis verwenden)
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs text-neutral-500">Basis wird aus den Touren berechnet.</span>
              <Button size="sm" variant="outline" onClick={() => update({ manuelle_basis: data.basis_brutto || 0 })} data-testid="payroll-manual-basis-set">
                Basis manuell überschreiben
              </Button>
            </div>
          )}
        </div>

        {/* Adjustments */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <Label className="text-sm uppercase tracking-wider text-neutral-700">Abzüge & Zulagen</Label>
            <div className="flex gap-1 flex-wrap">
              {ADJUSTMENT_TYPES.map((t) => (
                <Button key={t.value} size="sm" variant="outline" onClick={() => addAdjustment(t.value)} data-testid={`payroll-add-${t.value}`}>
                  <Plus className="h-3 w-3 mr-1" />{t.label}
                </Button>
              ))}
            </div>
          </div>
          {(data.adjustments || []).length === 0 && (
            <div className="text-xs text-neutral-500 italic">Keine Einträge.</div>
          )}
          {(data.adjustments || []).map((a, i) => {
            const meta = ADJUSTMENT_TYPES.find((t) => t.value === a.typ) || ADJUSTMENT_TYPES[0];
            return (
              <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:items-end mb-3 sm:mb-2 p-2 sm:p-0 border sm:border-0 border-neutral-200 rounded-md">
                <div className="sm:col-span-3">
                  <Label className="text-xs">Typ</Label>
                  <Select value={a.typ} onValueChange={(v) => updateAdjustment(i, { typ: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ADJUSTMENT_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-5">
                  <Label className="text-xs">Label</Label>
                  <Input value={a.label} onChange={(e) => updateAdjustment(i, { label: e.target.value })} data-testid={`payroll-adj-label-${i}`} />
                </div>
                <div className="sm:col-span-3">
                  <Label className="text-xs">Betrag CHF {a.typ === "abzug" ? "(–)" : "(+)"}</Label>
                  <Input type="number" step="0.01" value={a.betrag} onChange={(e) => updateAdjustment(i, { betrag: e.target.value })} className={`font-mono ${meta.color}`} data-testid={`payroll-adj-betrag-${i}`} />
                </div>
                <div className="sm:col-span-1 sm:pb-1 flex sm:block justify-end">
                  <Button size="sm" variant="ghost" onClick={() => removeAdjustment(i)} data-testid={`payroll-adj-remove-${i}`}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Payments */}
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <Label className="text-sm uppercase tracking-wider text-neutral-700">Auszahlungen</Label>
            <div className="flex gap-1 flex-wrap">
              {PAYMENT_METHODS.map((m) => (
                <Button key={m.value} size="sm" variant="outline" onClick={() => addPayment(m.value)} data-testid={`payroll-add-pay-${m.value}`}>
                  <Plus className="h-3 w-3 mr-1" />{m.label}
                </Button>
              ))}
            </div>
          </div>
          {(data.payments || []).length === 0 && (
            <div className="text-xs text-neutral-500 italic">Noch keine Auszahlung.</div>
          )}
          {(data.payments || []).map((p, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 sm:items-end mb-3 sm:mb-2 p-2 sm:p-0 border sm:border-0 border-neutral-200 rounded-md">
              <div className="col-span-1 sm:col-span-2">
                <Label className="text-xs">Methode</Label>
                <Select value={p.methode} onValueChange={(v) => updatePayment(i, { methode: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-1 sm:col-span-3">
                <Label className="text-xs">Betrag CHF</Label>
                <Input type="number" step="0.01" value={p.betrag} onChange={(e) => updatePayment(i, { betrag: e.target.value })} className="font-mono" data-testid={`payroll-pay-betrag-${i}`} />
              </div>
              <div className="col-span-1 sm:col-span-3">
                <Label className="text-xs">Datum</Label>
                <Input type="date" value={p.datum || ""} onChange={(e) => updatePayment(i, { datum: e.target.value })} className="font-mono" />
              </div>
              <div className="col-span-1 sm:col-span-3">
                <Label className="text-xs">Referenz</Label>
                <Input value={p.referenz || ""} onChange={(e) => updatePayment(i, { referenz: e.target.value })} placeholder="z.B. UBS / Twint-ID" />
              </div>
              <div className="col-span-2 sm:col-span-1 sm:pb-1 flex sm:block justify-end">
                <Button size="sm" variant="ghost" onClick={() => removePayment(i)}>
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </div>
          ))}
          <div className="flex gap-2 mt-3 flex-wrap">
            {PAYMENT_METHODS.map((m) => (
              <Button key={m.value} size="sm" variant="outline" onClick={() => payRemaining(m.value)} disabled={d.offen < 0.01} data-testid={`payroll-payfull-${m.value}`}>
                <Wallet className="h-3.5 w-3.5 mr-1" /> Rest via {m.label}
              </Button>
            ))}
            {d.offen < 0.01 && d.netto > 0 && (
              <Badge className="bg-[#008A00] text-white ml-2"><CheckCircle2 className="h-3 w-3 mr-1 inline" /> Voll bezahlt</Badge>
            )}
          </div>
        </div>

        <div className="space-y-1 mt-3">
          <Label>Bemerkung</Label>
          <Textarea value={data.bemerkung || ""} onChange={(e) => update({ bemerkung: e.target.value })} />
        </div>

        <DialogFooter className="flex-wrap gap-2">
          {data.id && !data.is_preview && (
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 mr-auto"
              onClick={async () => {
                if (!window.confirm("Diesen Lohn-Eintrag (inkl. Abzüge, Zulagen und Zahlungshistorie) wirklich löschen?")) return;
                try {
                  await api.delete(`/payroll-records/${data.id}`);
                  toast.success("Eintrag gelöscht.");
                  onClose();
                } catch (e) {
                  toast.error("Löschen fehlgeschlagen.");
                }
              }}
              data-testid="payroll-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Löschen
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/lohnabrechnung/${data.fahrer_id}/${data.monat}`;
              window.open(url, "_blank");
            }}
            data-testid="payroll-pdf"
          >
            Lohnabrechnung PDF
          </Button>
          <Button variant="outline" onClick={onClose}>Schliessen</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0055FF] hover:bg-[#0040CC]" data-testid="payroll-save">
            {saving ? "Speichere…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
